import { IGrid, IDirection, GridEvents } from "../types";
import { locateNodeByClassName } from "@dhx/ts-common/html";
import { isSortable } from "./main";

export function selectionMove(
	e: KeyboardEvent,
	grid: IGrid,
	dir: IDirection,
	range: number,
	toEnd = false,
	ctrlUp = false,
	shiftUp = false
) {
	if (grid.config.$editable || !grid.config.selection || locateNodeByClassName(e, "dhx_grid-header-cell")) {
		return;
	}
	if (e) {
		e.preventDefault();
	}
	const selected = grid.selection.getCell();
	const columns = grid.config.columns.filter(col => !col.hidden);
	grid.data.filter(row => !row.hidden);
	if (selected) {
		if (dir === "vertical") {
			if (toEnd) {
				const newItem =
					range === 1
						? grid.data.getItem(grid.data.getId(grid.data.getLength() - 1))
						: grid.data.getItem(grid.data.getId(0));
				grid.selection.setCell(newItem.id, selected.column.id, ctrlUp, shiftUp);
				grid.scrollTo(newItem.id.toString(), selected.column.id.toString());
			} else {
				const index = grid.data.getIndex(selected.row.id.toString());
				if (index + range >= 0 && index + range < grid.data.getLength()) {
					const newItem = grid.data.getItem(grid.data.getId(index + range));
					grid.selection.setCell(newItem.id, selected.column.id, ctrlUp, shiftUp);
					grid.scrollTo(newItem.id.toString(), selected.column.id.toString());
				}
			}
		} else {
			if (toEnd) {
				const newItem = range === 1 ? columns[columns.length - 1] : columns[0];
				grid.selection.setCell(selected.row.id, newItem.id, ctrlUp, shiftUp);
				grid.scrollTo(selected.row.id.toString(), newItem.id.toString());
			} else {
				const index = columns.indexOf(selected.column);
				if (index + range >= 0 && index + range < columns.length) {
					grid.selection.setCell(selected.row.id, columns[index + range].id, ctrlUp, shiftUp);
					grid.scrollTo(selected.row.id.toString(), columns[index + range].id.toString());
				}
			}
		}
	}
}

export function getKeysHandlers(grid) {
	const cellSelection =
		grid.config.selection === "cell" ||
		grid.config.selection === "complex" ||
		grid.config.selection === true;

	let gridBodyHandlers = {};

	if (grid.getRootView()) {
		const gridBody = grid.getRootView()?.refs?.grid_body?.el;
		if (!gridBody) {
			return;
		}
		gridBodyHandlers = {
			pageUp: e => {
				e.preventDefault();
				gridBody.scrollTop -= gridBody.clientHeight;
			},
			pageDown: e => {
				e.preventDefault();
				gridBody.scrollTop += gridBody.clientHeight;
			},
			home: e => {
				e.preventDefault();
				gridBody.scrollTop = 0;
			},
			end: e => {
				e.preventDefault();
				gridBody.scrollTop += gridBody.scrollHeight;
			},
		};
	}

	return {
		enter: e => {
			const $col = locateNodeByClassName(e, "dhx_grid-header-cell");
			if ($col) {
				// sort by enter on header
				const id = $col.getAttribute("dhx_id");
				const isResizable = e.target.getAttribute("dhx_resized");
				if (id) {
					const column = grid.getColumn(id);
					if (column && isSortable(grid.config, column) && !isResizable) {
						grid.events.fire(GridEvents.afterSort, [id]);
					}
				}
			}

			if (cellSelection) {
				const selected = grid.selection.getCell();
				if (
					selected &&
					((selected.column.editable !== false && grid.config.editable) || selected.column.editable)
				) {
					if (!grid.config.$editable) {
						if (selected.column.type !== "boolean") {
							grid.editCell(selected.row.id, selected.column.id, selected.column.editorType);
						} else {
							grid.events.fire(GridEvents.afterEditEnd, [
								!selected.row[selected.column.id],
								selected.row,
								selected.column,
							]);
						}
					} else {
						grid.editEnd();
					}
				}
			} else {
				if (grid.config.$editable) {
					grid.editEnd();
				}
			}
		},
		space: e => {
			const selected = grid.selection.getCell();
			if (
				cellSelection &&
				(selected?.column.editable ?? grid.config.editable) &&
				!grid.config.$editable
			) {
				if (selected && selected.column.type === "boolean") {
					e.preventDefault();
					grid.events.fire(GridEvents.afterEditEnd, [
						!selected.row[selected.column.id],
						selected.row,
						selected.column,
					]);
				}
			}
		},
		escape: () => {
			if (grid.config.$editable) {
				grid.editEnd(true);
			}
		},
		tab: e => {
			if (!grid.config.selection || locateNodeByClassName(e, "dhx_grid-header-cell")) {
				return;
			}
			if (grid.config.$editable) {
				grid.editEnd();
			}
			const selected = grid.selection.getCell();
			const columns = grid.config.columns.filter(col => !col.hidden);
			if (selected) {
				const index = columns.indexOf(selected.column) + 1;
				if (index >= 0 && index < columns.length) {
					e && e.preventDefault();
					grid.selection.setCell(selected.row.id, columns[index].id);
					grid.scrollTo(selected.row.id.toString(), columns[index].id.toString());
				} else if (index >= 0) {
					const newLineIndex = grid.data.getIndex(selected.row.id.toString()) + 1;
					if (newLineIndex < grid.data.getLength()) {
						e && e.preventDefault();
						grid.selection.setCell(grid.data.getId(newLineIndex), columns[0].id);
						grid.scrollTo(grid.data.getId(newLineIndex).toString(), columns[0].id.toString());
					}
				}
			}
		},
		"shift+tab": e => {
			if (!grid.config.selection || locateNodeByClassName(e, "dhx_grid-header-cell")) {
				return;
			}
			if (grid.config.$editable) {
				grid.editEnd();
			}
			const selected = grid.selection.getCell();
			const columns = grid.config.columns.filter(col => !col.hidden);
			if (selected) {
				const index = columns.indexOf(selected.column) - 1;
				if (index >= 0 && index < columns.length) {
					e && e.preventDefault();
					grid.selection.setCell(selected.row.id, columns[index].id);
					grid.scrollTo(selected.row.id.toString(), columns[index].id.toString());
				} else if (index < grid.data.getLength()) {
					const newLineIndex = grid.data.getIndex(selected.row.id.toString()) - 1;
					if (newLineIndex >= 0) {
						e && e.preventDefault();
						grid.selection.setCell(grid.data.getId(newLineIndex), columns[columns.length - 1].id);
						grid.scrollTo(
							grid.data.getId(newLineIndex).toString(),
							columns[columns.length - 1].id.toString()
						);
					}
				}
			}
		},
		arrowUp: e => {
			selectionMove(e, grid, "vertical", -1);
		},
		"ctrl+arrowUp": e => {
			selectionMove(e, grid, "vertical", -1, true);
		},
		"shift+arrowUp": e => {
			if (grid.config.multiselection) {
				selectionMove(e, grid, "vertical", -1, false, false, true);
			}
		},
		"ctrl+shift+arrowUp": e => {
			if (grid.config.multiselection) {
				selectionMove(e, grid, "vertical", -1, true, false, true);
			}
		},
		arrowDown: e => {
			selectionMove(e, grid, "vertical", 1);
		},
		"ctrl+arrowDown": e => {
			selectionMove(e, grid, "vertical", 1, true);
		},
		"shift+arrowDown": e => {
			if (grid.config.multiselection) {
				selectionMove(e, grid, "vertical", 1, false, false, true);
			}
		},
		"ctrl+shift+arrowDown": e => {
			if (grid.config.multiselection) {
				selectionMove(e, grid, "vertical", 1, true, false, true);
			}
		},
		arrowRight: e => {
			selectionMove(e, grid, "horizontal", 1);
		},
		"ctrl+arrowRight": e => {
			selectionMove(e, grid, "horizontal", 1, true);
		},
		"shift+arrowRight": e => {
			if (grid.config.multiselection) {
				selectionMove(e, grid, "horizontal", 1, false, false, true);
			}
		},
		"ctrl+shift+arrowRight": e => {
			if (grid.config.multiselection) {
				selectionMove(e, grid, "horizontal", 1, true, false, true);
			}
		},
		arrowLeft: e => {
			selectionMove(e, grid, "horizontal", -1);
		},
		"ctrl+arrowLeft": e => {
			selectionMove(e, grid, "horizontal", -1, true);
		},
		"shift+arrowLeft": e => {
			if (grid.config.multiselection) {
				selectionMove(e, grid, "horizontal", -1, false, false, true);
			}
		},
		"ctrl+shift+arrowLeft": e => {
			if (grid.config.multiselection) {
				selectionMove(e, grid, "horizontal", -1, true, false, true);
			}
		},
		...gridBodyHandlers,
	};
}
